import {
  StyledErrorIcon,
  StyledError,
  StyledLeftContainer,
  StyledRightContainer,
  StyledTextField,
  StyledTextFieldContainer,
} from "./text-field.style";
import { MDSTextFieldProps, MDSTextFieldSlotName, MDSTextFieldVariant } from "./text-field.types";
import { getSlot, getSlots } from "@/mds/utils/slots";
import { Input, TextArea } from "react-aria-components";
import { MdsIconKind } from "@/design-system/components/icon";
import { FormEventHandler, forwardRef, useCallback, useRef, useState } from "react";
import {
  ReactChangeEvent,
  ReactFocusEvent,
  ReactKeyboardEvent,
} from "@/design-system/constants/handlers/types";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { KeyboardCode } from "@/design-system/constants/keyboard/types";
import { useMergeRefs } from "@floating-ui/react";

export const MDSTextField = forwardRef(function MDSTextField(
  {
    children,
    placeholder,
    isTextArea,
    textAreaProps,
    inputProps,
    variant,
    onFocus,
    onBlur,
    onChange,
    onPointerEnter,
    onPointerLeave,
    value,
    errorMessage,
    onClearText,
    showClearTextButton,
    onSubmit,
    isDisabled,
    ...props
  }: MDSTextFieldProps,
  ref
) {
  const leftAddons = getSlots(children, MDSTextFieldSlotName.LeftAddon);
  const rightAddon = getSlot(children, MDSTextFieldSlotName.RightAddon);

  const innerRef = useRef<HTMLInputElement>(null);
  const combinedRef = useMergeRefs([ref, innerRef]);

  const handleClearText = useCallback(() => {
    if (!onClearText) return;
    onClearText?.();
    if (innerRef.current) {
      innerRef.current.value = "";
      innerRef.current.focus();
    }
  }, [onClearText]);

  const onKeyDown = isTextArea ? textAreaProps?.onKeyDown : inputProps?.onKeyDown;

  const handleKeyDown: FormEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const keyCode = event.code;
      if (onClearText && keyCode === KeyboardCode.Escape) {
        event.stopPropagation();
        onClearText();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onKeyDown?.(event as any);
    },
    [onClearText, onKeyDown]
  );

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(
    (e: ReactFocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: ReactFocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const [isEmpty, setIsEmpty] = useState(!value);

  const handleChange = useCallback(
    (e: ReactChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsEmpty(!e.target.value);
      onChange?.(e.target.value);
    },
    [onChange]
  );

  const [isHovering, setIsHovering] = useState(false);

  const handlePointerEnter: React.PointerEventHandler<HTMLDivElement> = useCallback(
    e => {
      setIsHovering(true);
      onPointerEnter?.(e);
    },
    [onPointerEnter]
  );

  const handlePointerLeave: React.PointerEventHandler<HTMLDivElement> = useCallback(
    e => {
      setIsHovering(false);
      onPointerLeave?.(e);
    },
    [onPointerLeave]
  );

  const handlePointerEnterBrand: React.PointerEventHandler<HTMLDivElement> = useCallback(
    e => {
      onPointerEnter?.(e);
    },
    [onPointerEnter]
  );

  const handlePointerLeaveBrand: React.PointerEventHandler<HTMLDivElement> = useCallback(
    e => {
      onPointerLeave?.(e);
    },
    [onPointerLeave]
  );

  const canSend = !isDisabled && !isEmpty;

  return (
    <>
      <StyledTextFieldContainer
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        <StyledTextField
          $variant={variant}
          $isFocused={isFocused || isHovering}
          $isTextArea={isTextArea}
          $hasError={!!errorMessage}
          {...props}
          ref={undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={props["aria-label"] ?? "text field"}
        >
          {leftAddons.map((leftAddon, index) => (
            <StyledLeftContainer $variant={variant} key={index}>
              {leftAddon}
            </StyledLeftContainer>
          ))}

          {isTextArea ? (
            <TextArea
              {...textAreaProps}
              placeholder={placeholder}
              ref={combinedRef as React.LegacyRef<HTMLTextAreaElement> | null}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <Input
              {...inputProps}
              placeholder={placeholder}
              ref={combinedRef as React.LegacyRef<HTMLInputElement> | null}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          )}

          {rightAddon && (
            <StyledRightContainer $variant={variant}>{rightAddon}</StyledRightContainer>
          )}
          {!rightAddon && showClearTextButton && (
            <StyledRightContainer $variant={variant}>
              <MDSIconButton
                variant={MDSIconButtonVariant.Transparent}
                size={MDSIconButtonSize.XSmall}
                iconKind={MdsIconKind.Exit}
                onClick={handleClearText}
                isDisabled={isEmpty}
              />
            </StyledRightContainer>
          )}
          {!rightAddon && !showClearTextButton && variant === MDSTextFieldVariant.Brand && (
            <StyledRightContainer
              $variant={variant}
              onPointerEnter={handlePointerEnterBrand}
              onPointerLeave={handlePointerLeaveBrand}
            >
              <MDSIconButton
                iconKind={MdsIconKind.ArrowBrand}
                onClick={onSubmit}
                isDisabled={!canSend}
                variant={MDSIconButtonVariant.Brand}
                rounded
                data-test-id="text-field-submit-button"
              />
            </StyledRightContainer>
          )}
        </StyledTextField>
      </StyledTextFieldContainer>
      {errorMessage && (
        <StyledError>
          <StyledErrorIcon kind={MdsIconKind.InfoCircle} />
          <MDSText
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.Medium}
            color={MDSTextColor.Danger}
          >
            {errorMessage}
          </MDSText>
        </StyledError>
      )}
    </>
  );
});
